import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux-hooks';
import AgentService from '../services/agent.service';
import { TChatMsg } from '../types/chat.types';
import { setChatData } from '../store/chat.slice';
import { v4 as uuidv4 } from 'uuid';

const Suggestions: FC<{answerLoaded: boolean}> = ({ answerLoaded }) => {
  const dispatch = useAppDispatch();
  const { chatData, chatLoading } = useAppSelector((state) => state.chat);
  const { agentInfo } = useAppSelector((state) => state.common);
  const [relatedQuestions, setRelatedQuestions] = useState<string[]>([]);
  const lastProcessedMsgId = useRef<string | null>(null);

  const lastQues = useMemo(() => {
    if (!chatData || chatData.length === 0) return null;
    return chatData[chatData.length - 1];
  }, [chatData]);

  // Memoize the last question to prevent re-fetching during streaming
  const lastQuesMsgId = useMemo(() => lastQues?.msgId, [lastQues?.msgId]);
  const lastQuesMsg = useMemo(() => lastQues?.ques, [lastQues?.ques]);

  useEffect(() => {
    const getSuggestions = async () => {
      // Fetch suggestions immediately when question is asked (don't wait for answer)
      if (!lastQuesMsgId || !lastQuesMsg || !agentInfo) {
        return;
      }

      // Prevent duplicate calls for the same question
      if (lastProcessedMsgId.current === lastQuesMsgId) {
        return;
      }

      lastProcessedMsgId.current = lastQuesMsgId;
      setRelatedQuestions([]);

      try {
        const orgId = agentInfo.createdByOrg?._id;
        const assistantId = agentInfo._id;

        if (!orgId || !assistantId) {
          setRelatedQuestions([]);
          return;
        }

        const response = await AgentService.getPineconeSuggestions(
          lastQuesMsg,
          orgId,
          assistantId
        );

        if (response.data && response.data.data?.suggestions) {
          setRelatedQuestions(response.data.data.suggestions);
        } else {
          setRelatedQuestions([]);
        }
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setRelatedQuestions([]);
      }
    };

    getSuggestions();
  }, [lastQuesMsgId, lastQuesMsg, agentInfo]);

  const handleSelectQuestion = (question: string) => {
    if (!agentInfo) return;

    const msgId = uuidv4();
    const newChat: TChatMsg = {
      msgId,
      ques: question,
      answer: '',
      model: '',
      chatId: '',
      assistantId: agentInfo._id || '',
    };

    dispatch(setChatData([...chatData, newChat]));
  };

  // Don't show if loading, no questions, or answer not yet loaded
  if (chatLoading || !relatedQuestions || relatedQuestions.length === 0 || !answerLoaded) {
    return null;
  }

  return (
    <div className="mt-4 animate-fade-in-up">
      <div className="text-[#333] dark:text-[#bdbdbd] font-semibold text-[14px] sm:text-[16px] mb-3 flex items-center">
        <span>Related Questions</span>
      </div>

      {relatedQuestions.map((item, index) => (
        <div
          key={`suggestion-${index}`}
          className="w-full mb-2 text-[12px] sm:text-[14px] font-medium flex justify-between items-center border-b pb-2 dark:text-[#bdbdbd] dark:border-b-[#444] border-b-[#ccc] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-1 rounded transition-colors"
          onClick={() => handleSelectQuestion(item)}
        >
          <div className="pr-2 sm:pr-6">{item}</div>
          <i className="icon-plus text-[10px] font-semibold shrink-0 dark:text-[#bdbdbd]"></i>
        </div>
      ))}
    </div>
  );
};

export default Suggestions;
